/**
 * ButtonCluster — universal horizontal button cluster primitive (Push 4-1g-hardaudit lock).
 *
 * THE RULE (structural, not CSS-selector patching):
 *   Any horizontal cluster of 2+ buttons wraps in `<ButtonCluster>`. The
 *   primitive guarantees every direct-child button — regardless of variant
 *   (`.btn`, `.btn-primary`, `.btn-secondary`, `.btn-danger`, `.btn-link`,
 *   an anchor styled as button, or a raw `<button>`) — renders
 *   at the same computed envelope: height, padding, border-radius, border-
 *   width, font-size, font-weight, line-height. The button variant retains
 *   its color identity (filled teal, outlined red, transparent link) but
 *   the pill shape is uniform.
 *
 * Why this instead of the prior `.button-row` / `.record-actions-group` CSS
 * pattern:
 *   Prior CSS selectors (`.button-row > .btn { min-height: 34px }`) only
 *   equalized MIN height, not the total envelope. A `.btn-link` sibling
 *   (0 border, 0 padding, transparent) next to a `.btn-primary` (1px border,
 *   14px padding, filled) would land at the same height but read as different
 *   weights — border-width mismatch, padding mismatch, font-size mismatch
 *   all still leaked through. `<ButtonCluster>` applies a hard `height` +
 *   `padding` + `border-width` override to EVERY direct child, so the pill
 *   envelope is truly uniform. Border-width uses `1px solid transparent`
 *   on filled variants + `1px solid <color>` on outlined variants — either
 *   way the box-model reserves 1px for a border, so no shift.
 *
 * Slot ordering + overflow:
 *   Children render left-to-right in JSX order. The `alignment` prop controls
 *   flex justification (start / end / between / center). When `maxInline` is
 *   set and children count exceeds it, the last (children.length - maxInline)
 *   children collapse into `<ActionOverflow>` (the shared "More ▾" primitive).
 *   For record-detail hero rows this is already handled by `<RecordActions>`
 *   which uses ButtonCluster internally; general clusters that want overflow
 *   pass `maxInline={N}`.
 *
 * Usage:
 *   // Two-button footer
 *   <ButtonCluster alignment="end">
 *     <button className="btn btn-secondary" onClick={cancel}>Cancel</button>
 *     <button className="btn btn-primary" onClick={save}>Save</button>
 *   </ButtonCluster>
 *
 *   // Dense list-row cluster
 *   <ButtonCluster size="sm">
 *     <button className="btn btn-secondary">Resolve</button>
 *     <button className="btn btn-secondary">Reject</button>
 *   </ButtonCluster>
 *
 *   // Cluster with variety of variants — all normalize
 *   <ButtonCluster size="sm">
 *     <button className="btn btn-primary">Buy credits</button>
 *     <button className="btn btn-secondary">Adjust limit</button>
 *     <button className="btn btn-link">Dismiss</button>
 *   </ButtonCluster>
 *
 * Prop contract:
 *   - `size`: 'md' (default, 34px) | 'sm' (28px). Every child gets sized.
 *   - `alignment`: 'start' | 'end' | 'between' | 'center'. Default 'start'.
 *   - `gap`: pixel gap between children. Default 8.
 *   - `maxInline`: if set, children past this index collapse into overflow.
 *   - `overflowTriggerLabel`: label for the "More ▾" trigger. Default 'More'.
 *   - `className`: appended for opt-in extra styling.
 *
 * See PATTERNS.md "Button cluster same-height rule" for the discipline.
 */

import type { ReactNode } from 'react';

import {
  ActionOverflow,
  type ActionOverflowItem,
} from './ActionOverflow.tsx';

interface ButtonClusterProps {
  children: ReactNode;
  /** Pill height envelope. md = 34px (default), sm = 28px. */
  size?: 'md' | 'sm';
  /** Flex justification. Default 'start'. */
  alignment?: 'start' | 'end' | 'between' | 'center';
  /** Pixel gap between children. Default 8. */
  gap?: number;
  /**
   * When set, children past this index collapse into an ActionOverflow.
   * Requires the overflowed children to be normal ReactElements with
   * onClick + child text (they'll be converted into ActionOverflowItems).
   * For complex overflow logic (copy actions, Link `to`), pre-compute your
   * own overflow via <ActionOverflow> and pass only inline items here.
   */
  maxInline?: number;
  /** Label for the "More ▾" trigger when maxInline overflow fires. */
  overflowTriggerLabel?: string;
  /** Extra className appended to the wrapper (e.g. 'my-custom-cluster'). */
  className?: string;
}

export function ButtonCluster({
  children,
  size = 'md',
  alignment = 'start',
  gap = 8,
  maxInline,
  overflowTriggerLabel = 'More',
  className,
}: ButtonClusterProps) {
  // Normalize children into an array so we can slice for overflow.
  // React.Children.toArray flattens fragments + filters null/undefined/false.
  const asArray = normalizeChildren(children);

  const alignmentClass =
    alignment === 'end'
      ? ' button-cluster-align-end'
      : alignment === 'between'
        ? ' button-cluster-align-between'
        : alignment === 'center'
          ? ' button-cluster-align-center'
          : '';

  const sizeClass = size === 'sm' ? ' button-cluster-sm' : '';

  const wrapperClass =
    `button-cluster${sizeClass}${alignmentClass}` +
    (className !== undefined && className !== '' ? ` ${className}` : '');

  // No overflow requested — render straight through.
  if (maxInline === undefined || asArray.length <= maxInline) {
    return (
      <div className={wrapperClass} style={{ gap }}>
        {asArray}
      </div>
    );
  }

  // Overflow branch: keep the first maxInline items inline, collapse the rest
  // into an ActionOverflow "More ▾" trigger.
  const inline = asArray.slice(0, maxInline);
  const overflow = asArray.slice(maxInline);
  const overflowItems: ActionOverflowItem[] = overflow
    .map(childToOverflowItem)
    .filter((item): item is ActionOverflowItem => item !== null);

  return (
    <div className={wrapperClass} style={{ gap }}>
      {inline}
      {overflowItems.length > 0 && (
        <ActionOverflow
          items={overflowItems}
          triggerLabel={overflowTriggerLabel}
          size={size}
        />
      )}
    </div>
  );
}

/**
 * Convert a ReactNode array into a filtered array of visible children.
 * Falsy children (null / undefined / false) are dropped; fragments are
 * flattened by React.Children.toArray.
 */
function normalizeChildren(children: ReactNode): ReactNode[] {
  const out: ReactNode[] = [];
  const walk = (node: ReactNode): void => {
    if (node === null || node === undefined || node === false || node === true) {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    out.push(node);
  };
  walk(children);
  return out;
}

/**
 * Best-effort conversion of an inline React child (typically a
 * `<button>` or `<Link>`) into an ActionOverflowItem. Only preserves
 * `onClick` (drops `to` link semantics, drops `copy` handlers). Complex
 * overflow behavior should pre-compute via a direct <ActionOverflow>.
 */
function childToOverflowItem(child: ReactNode): ActionOverflowItem | null {
  if (
    child === null ||
    child === undefined ||
    typeof child === 'boolean' ||
    typeof child === 'string' ||
    typeof child === 'number'
  ) {
    return null;
  }
  // React element narrowing without pulling in React namespace types.
  const el = child as {
    props?: {
      onClick?: () => void;
      children?: ReactNode;
      title?: string;
      disabled?: boolean;
    };
  };
  const label = extractText(el.props?.children);
  if (label === null) return null;
  const item: ActionOverflowItem = {
    label,
    onClick: el.props?.onClick ?? (() => undefined),
  };
  if (el.props?.disabled === true) item.disabled = true;
  if (el.props?.title !== undefined) item.title = el.props.title;
  return item;
}

function extractText(node: ReactNode): string | null {
  if (node === null || node === undefined) return null;
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    const parts = node.map(extractText).filter((s): s is string => s !== null);
    return parts.length > 0 ? parts.join(' ') : null;
  }
  return null;
}
